import {
  ArrowLeft,
  Check,
  CheckCircle2,
  CircleAlert,
  Copy,
  Loader2,
  RefreshCw,
  TriangleAlert,
} from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router";
import { toast } from "sonner";

import { Alert, AlertDescription, AlertTitle } from "~/components/ui/alert";
import { Button } from "~/components/ui/button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "~/components/ui/dialog";
import type {
  CodeStatusResponseDto,
  PatientReadinessDto,
  SessionReadinessResponseDto,
} from "~/lib/api";
import {
  fetchCodeStatus,
  fetchSessionReadiness,
  generateHandoverCode,
  regenerateHandoverCode,
} from "~/lib/codes";
import { formatRemaining, useCountdown } from "~/lib/countdown";
import { fetchCurrentUserId } from "~/lib/patients";
import { isOutgoingDoctor } from "~/lib/permissions";
import { cn } from "~/lib/utils";
import NotFoundRoute from "~/routes/not-found";
import { PRIORITY_META } from "~/session/patient-list/priority";
import { useSession } from "~/session/session-context";
import { SendCodeSmsDialog } from "~/session/start-handover/send-code-sms-dialog";

type Phase =
  | { kind: "loading" }
  | { kind: "not-found" }
  | { kind: "error"; message: string }
  // The access gate failed: render a 403-style explanation rather than the
  // confirmation screen. `reason` is the user-facing sentence.
  | { kind: "denied"; reason: string }
  // The pre-generation checklist (HAN-64): the per-patient readiness summary. The
  // generate CTA is disabled until `readiness.ready`, with per-patient reasons.
  | { kind: "confirm"; readiness: SessionReadinessResponseDto }
  // The outgoing doctor came back to an already-active handover (HAN-89): show
  // the (non-secret) code state and the option to regenerate. The plaintext is
  // NOT here — it's unrecoverable by design.
  | { kind: "manage"; status: CodeStatusResponseDto }
  // The plaintext code lives ONLY here, in component state — never localStorage
  // or sessionStorage. A refresh loses it, which is the intended one-time
  // behaviour the warning banner explains. `regenerated` toggles the copy
  // between first-generation and post-regeneration.
  | { kind: "generated"; code: string; expiresAt: string; regenerated: boolean };

/**
 * Start-handover screen (E6.4): the outgoing doctor's view after they hit
 * "Start handover". It first confirms (showing the patient count) — we never
 * auto-generate on mount — then, on the doctor's click, calls
 * `POST /sessions/:id/code` and displays the one-time code prominently with a
 * live countdown.
 *
 * Rendered inside `<SessionLayout>`, so the session (and the caller's ward
 * access) is already resolved. The route is gated to the outgoing doctor on a
 * draft session with at least one patient; any other access lands on an
 * explanation. The API enforces the identical rules — this gate only keeps the
 * UI from offering an action the caller can't complete.
 */
export function StartHandoverScreen(): JSX.Element {
  const { session } = useSession();
  const sessionId = session.id;

  const [state, setState] = useState<Phase>({ kind: "loading" });
  const [reloadKey, setReloadKey] = useState(0);
  const [isGenerating, setIsGenerating] = useState(false);
  const [generateError, setGenerateError] = useState<string | null>(null);

  const load = useCallback(
    async (signal: { cancelled: boolean }): Promise<void> => {
      setState({ kind: "loading" });

      const currentUserId = await fetchCurrentUserId();
      if (signal.cancelled) return;

      // Access gate. Distinct, helpful explanations for each failed condition,
      // since the patient list links here only for the outgoing doctor and only
      // with patients — anyone else has navigated here directly.
      if (!isOutgoingDoctor(currentUserId ? { id: currentUserId } : null, session)) {
        setState({
          kind: "denied",
          reason: "Only the doctor who started this handover can generate a code.",
        });
        return;
      }
      if (session.status === "locked") {
        setState({ kind: "denied", reason: "This handover session has been closed." });
        return;
      }
      if (session.status === "active") {
        // The outgoing doctor is back on an active handover (a reload, a
        // returned tab). The plaintext is gone, but they can see the code's
        // state and regenerate a fresh one (HAN-89) rather than abandoning the
        // session. Fetch the non-secret status to drive that view.
        const statusResult = await fetchCodeStatus(sessionId);
        if (signal.cancelled) return;
        if (statusResult.status === "not-found") {
          setState({ kind: "not-found" });
          return;
        }
        if (statusResult.status === "denied") {
          setState({
            kind: "denied",
            reason: "Only the doctor who started this handover can manage its code.",
          });
          return;
        }
        if (statusResult.status === "error") {
          setState({
            kind: "error",
            message: "Couldn't load the handover code status. Please try again.",
          });
          return;
        }
        setState({ kind: "manage", status: statusResult.data });
        return;
      }

      // Draft path: load the final readiness checklist (HAN-64). The server is
      // the source of truth for per-patient completeness; the screen renders it
      // and the generate CTA stays disabled until every patient is ready.
      const readinessResult = await fetchSessionReadiness(sessionId);
      if (signal.cancelled) return;
      if (readinessResult.status === "not-found") {
        setState({ kind: "not-found" });
        return;
      }
      if (readinessResult.status === "denied") {
        setState({
          kind: "denied",
          reason: "Only the doctor who started this handover can generate a code.",
        });
        return;
      }
      if (readinessResult.status === "error") {
        setState({
          kind: "error",
          message: "Couldn't load the handover summary. Please try again.",
        });
        return;
      }
      if (readinessResult.data.patientCount === 0) {
        setState({
          kind: "denied",
          reason: "Add at least one patient before starting the handover.",
        });
        return;
      }

      setState({ kind: "confirm", readiness: readinessResult.data });
    },
    [sessionId, session],
  );

  useEffect(() => {
    const signal = { cancelled: false };
    void load(signal);
    return () => {
      signal.cancelled = true;
    };
  }, [load, reloadKey]);

  const handleGenerate = async (): Promise<void> => {
    setGenerateError(null);
    setIsGenerating(true);
    const result = await generateHandoverCode(sessionId);
    setIsGenerating(false);
    if (result.status === "error") {
      // Surface the error and stay on the confirmation screen so the doctor can
      // retry.
      setGenerateError(result.message);
      return;
    }
    setState({
      kind: "generated",
      code: result.data.code,
      expiresAt: result.data.expiresAt,
      regenerated: false,
    });
  };

  // Called by the manage view once a fresh code is minted: swap to the one-time
  // display, flagged as a regeneration so the copy reflects "new code".
  const handleRegenerated = (data: { code: string; expiresAt: string }): void => {
    setState({
      kind: "generated",
      code: data.code,
      expiresAt: data.expiresAt,
      regenerated: true,
    });
  };

  if (state.kind === "loading") {
    return (
      <div className="flex items-center justify-center py-16" aria-live="polite">
        <Loader2
          className="h-6 w-6 animate-spin text-muted-foreground"
          aria-label="Loading handover"
        />
      </div>
    );
  }

  if (state.kind === "not-found") {
    return <NotFoundRoute />;
  }

  if (state.kind === "error") {
    return (
      <Alert variant="destructive">
        <AlertTitle>Something went wrong</AlertTitle>
        <AlertDescription className="flex flex-col items-start gap-3">
          <span>{state.message}</span>
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              setReloadKey((k) => k + 1);
            }}
          >
            Try again
          </Button>
        </AlertDescription>
      </Alert>
    );
  }

  if (state.kind === "denied") {
    return (
      <div className="mx-auto max-w-2xl space-y-6">
        <Link
          to={`/session/${sessionId}`}
          className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-4 w-4" aria-hidden="true" />
          Back to patients
        </Link>
        <Alert variant="destructive">
          <AlertTitle>You can&rsquo;t start this handover</AlertTitle>
          <AlertDescription>{state.reason}</AlertDescription>
        </Alert>
      </div>
    );
  }

  if (state.kind === "manage") {
    return (
      <ManageCode sessionId={sessionId} status={state.status} onRegenerated={handleRegenerated} />
    );
  }

  if (state.kind === "generated") {
    return (
      <CodeDisplay
        sessionId={sessionId}
        code={state.code}
        expiresAt={state.expiresAt}
        regenerated={state.regenerated}
      />
    );
  }

  // Confirmation screen — the final readiness checklist (HAN-64).
  const { readiness } = state;
  const incompleteCount = readiness.patients.filter((p) => !p.complete).length;

  return (
    <div className="mx-auto max-w-2xl space-y-6">
      <Link
        to={`/session/${sessionId}`}
        className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="h-4 w-4" aria-hidden="true" />
        Back to patients
      </Link>

      <header className="space-y-2">
        <h1 className="text-2xl font-semibold tracking-tight">Start handover</h1>
        <p className="text-sm text-muted-foreground">
          Review every patient before you hand over{" "}
          <span className="font-medium text-foreground">
            {readiness.patientCount} {readiness.patientCount === 1 ? "patient" : "patients"}
          </span>
          . Generating a code makes this handover available to the incoming doctor.
        </p>
      </header>

      <ReadinessChecklist patients={readiness.patients} />

      {readiness.ready ? null : (
        <Alert variant="destructive" data-testid="not-ready-alert">
          <CircleAlert className="h-4 w-4" aria-hidden="true" />
          <AlertTitle>
            {incompleteCount} {incompleteCount === 1 ? "patient is" : "patients are"} not ready
          </AlertTitle>
          <AlertDescription>
            Every patient needs a complete SBAR handover, and high-priority patients need at least
            one task, before you can generate the code.
          </AlertDescription>
        </Alert>
      )}

      {generateError ? (
        <Alert variant="destructive">
          <AlertTitle>Couldn&rsquo;t generate the code</AlertTitle>
          <AlertDescription>{generateError}</AlertDescription>
        </Alert>
      ) : null}

      <Button
        onClick={() => {
          void handleGenerate();
        }}
        // Hard stop: the server enforces the same rule, but disabling here keeps
        // the doctor from issuing a request that can only fail.
        disabled={isGenerating || !readiness.ready}
      >
        {isGenerating ? <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" /> : null}
        Generate handover code
      </Button>
    </div>
  );
}

/**
 * The pre-generation summary (HAN-64): one row per patient with its priority,
 * whether the SBAR is complete, how many tasks it carries, and — when the patient
 * isn't ready — the specific reasons. Incomplete patients are flagged so the
 * outgoing doctor knows exactly what to fix before the code can be generated.
 */
function ReadinessChecklist({ patients }: { patients: PatientReadinessDto[] }): JSX.Element {
  return (
    <ul className="space-y-3" data-testid="readiness-checklist">
      {patients.map((patient) => {
        const meta = PRIORITY_META[patient.priority];
        return (
          <li
            key={patient.id}
            data-testid="readiness-row"
            data-complete={patient.complete}
            className={cn(
              "rounded-md border px-4 py-3",
              patient.complete ? "border-border" : "border-destructive/40 bg-destructive/5",
            )}
          >
            <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1">
              <div className="flex items-center gap-2">
                {patient.complete ? (
                  <CheckCircle2 className="h-5 w-5 shrink-0 text-priority-low" aria-label="Ready" />
                ) : (
                  <CircleAlert
                    className="h-5 w-5 shrink-0 text-destructive"
                    aria-label="Not ready"
                  />
                )}
                <span className="font-medium">{patient.fullName}</span>
                <span
                  className={`shrink-0 rounded-full px-2.5 py-0.5 text-xs font-semibold ${meta.pillClass}`}
                >
                  {meta.label}
                </span>
              </div>
              <div className="flex items-center gap-3 text-sm text-muted-foreground">
                <span className={patient.sbarComplete ? undefined : "font-medium text-destructive"}>
                  {patient.sbarComplete ? "SBAR complete" : "SBAR incomplete"}
                </span>
                <span aria-hidden="true">·</span>
                <span>
                  {patient.taskCount} {patient.taskCount === 1 ? "task" : "tasks"}
                </span>
              </div>
            </div>
            {patient.complete ? null : (
              <ul className="mt-2 list-disc space-y-0.5 pl-10 text-sm text-destructive">
                {patient.issues.map((issue) => (
                  <li key={issue.code}>{issue.message}</li>
                ))}
              </ul>
            )}
          </li>
        );
      })}
    </ul>
  );
}

/**
 * Copy `text` to the clipboard, returning whether it landed.
 *
 * Prefers the async Clipboard API (the only option in secure contexts), but
 * falls back to a throwaway off-screen `<textarea>` + `execCommand("copy")` for
 * the cases where it's unavailable — insecure origins (plain HTTP), older
 * browsers, or a permission denial. If both routes fail we return `false` so the
 * caller can tell the doctor to copy the code by hand rather than silently
 * pretend it worked.
 */
async function writeToClipboard(text: string): Promise<boolean> {
  if (typeof navigator !== "undefined" && navigator.clipboard?.writeText) {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch {
      // Fall through to the legacy path below.
    }
  }

  try {
    const textarea = document.createElement("textarea");
    textarea.value = text;
    textarea.setAttribute("readonly", "");
    textarea.style.position = "fixed";
    textarea.style.left = "-9999px";
    document.body.appendChild(textarea);
    textarea.select();
    const ok = document.execCommand("copy");
    document.body.removeChild(textarea);
    return ok;
  } catch {
    return false;
  }
}

/**
 * A copy-to-clipboard button for the displayed handover code (HAN-65). It's a
 * real `<button>`, so it's keyboard-operable for free, and carries an
 * `aria-label` that names the code being copied. On success we flash a "Copied"
 * affirmation on the button itself and a toast; on failure (clipboard blocked
 * everywhere) we tell the doctor to copy it manually.
 */
function CopyCodeButton({ code }: { code: string }): JSX.Element {
  const [copied, setCopied] = useState(false);

  const handleCopy = useCallback(async () => {
    const ok = await writeToClipboard(code);
    if (ok) {
      setCopied(true);
      toast.success("Code copied to clipboard.");
      window.setTimeout(() => setCopied(false), 2000);
    } else {
      toast.error("Couldn't copy automatically — copy the code manually.");
    }
  }, [code]);

  return (
    <Button
      type="button"
      variant="outline"
      onClick={handleCopy}
      aria-label={`Copy handover code ${code} to clipboard`}
    >
      {copied ? (
        <Check className="h-4 w-4" aria-hidden="true" />
      ) : (
        <Copy className="h-4 w-4" aria-hidden="true" />
      )}
      {copied ? "Copied" : "Copy code"}
    </Button>
  );
}

/**
 * The one-time code display. The countdown is purely cosmetic — the server is the
 * authority on expiry — but when it reaches zero we swap the code out for an
 * "expired" message so the doctor isn't left reading a code that no longer works.
 * We never auto-generate a replacement; starting again is a deliberate act.
 */
function CodeDisplay({
  sessionId,
  code,
  expiresAt,
  regenerated,
}: {
  sessionId: string;
  code: string;
  expiresAt: string;
  regenerated: boolean;
}): JSX.Element {
  const remainingMs = useCountdown(expiresAt);
  const expired = remainingMs <= 0;

  return (
    <div className="mx-auto max-w-2xl space-y-8 text-center">
      <header className="space-y-2">
        <h1 className="text-2xl font-semibold tracking-tight">
          {regenerated ? "New handover code" : "Handover code"}
        </h1>
        {regenerated ? (
          <p className="text-sm text-muted-foreground" role="status">
            The previous code no longer works. Share this new one instead.
          </p>
        ) : null}
      </header>

      {expired ? (
        <p className="text-lg text-muted-foreground" role="status">
          This code has expired.{" "}
          {regenerated
            ? "Refresh this page to regenerate a new one."
            : "You may need to start a new session."}
        </p>
      ) : (
        <>
          <div className="space-y-4">
            <p
              className="font-mono text-7xl font-bold tracking-widest tabular-nums"
              data-testid="handover-code"
            >
              {code}
            </p>
            <div className="flex flex-wrap items-center justify-center gap-2">
              <CopyCodeButton code={code} />
              <SendCodeSmsDialog sessionId={sessionId} code={code} />
            </div>
          </div>
          <p className="text-sm text-muted-foreground">
            Tell your colleague this code or text it to them. Expires in{" "}
            <span className="font-medium tabular-nums text-foreground" aria-live="off">
              {formatRemaining(remainingMs)}
            </span>
            .
          </p>
        </>
      )}

      {/* Static amber warning — the code is shown only once and is held in memory
          alone, so a refresh loses it. After a refresh the outgoing doctor can
          still regenerate a fresh one from this page (HAN-89). */}
      <div
        className="flex items-start gap-3 rounded-md border border-amber-300 bg-amber-50 px-4 py-3 text-left text-sm text-amber-900"
        data-testid="refresh-warning"
      >
        <TriangleAlert className="mt-0.5 h-5 w-5 shrink-0" aria-hidden="true" />
        <span>
          <strong className="font-semibold">Do not refresh this page</strong> — the code is shown
          only once. If you lose it, come back here to regenerate a new code.
        </span>
      </div>

      <div>
        <Link
          to={`/session/${sessionId}`}
          className="inline-flex items-center gap-1 text-sm font-medium text-primary underline-offset-4 hover:underline"
        >
          Continue to handover view &rarr;
        </Link>
      </div>
    </div>
  );
}

/**
 * The "manage an active handover's code" view (HAN-89): what the outgoing doctor
 * sees when they come back to a session whose code has already been generated.
 * The plaintext is gone for good, so this shows only its *state* — a live expiry
 * countdown, remaining attempts, and whether it's been picked up — plus a
 * confirmed regenerate that mints a fresh code (invalidating the old one).
 *
 * Regenerate is hidden once the incoming doctor has used the code: the handover
 * is in flight and a new code would only confuse them (the API also refuses it).
 */
function ManageCode({
  sessionId,
  status,
  onRegenerated,
}: {
  sessionId: string;
  status: CodeStatusResponseDto;
  onRegenerated: (data: { code: string; expiresAt: string }) => void;
}): JSX.Element {
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [isRegenerating, setIsRegenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const remainingMs = useCountdown(status.expiresAt ?? 0);
  // The server is the authority on expiry; the countdown is cosmetic. Treat the
  // code as expired if the server already said so or the countdown has run out.
  const expired = !status.exists || status.expired || remainingMs <= 0;
  const used = status.usedAt !== null;
  const locked = status.lockedUntil !== null && new Date(status.lockedUntil).getTime() > Date.now();
  const canRegenerate = !used;

  const handleRegenerate = async (): Promise<void> => {
    setError(null);
    setIsRegenerating(true);
    const result = await regenerateHandoverCode(sessionId);
    setIsRegenerating(false);
    setConfirmOpen(false);
    if (result.status === "error") {
      setError(result.message);
      return;
    }
    onRegenerated(result.data);
  };

  return (
    <div className="mx-auto max-w-2xl space-y-6">
      <Link
        to={`/session/${sessionId}`}
        className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="h-4 w-4" aria-hidden="true" />
        Back to patients
      </Link>

      <header className="space-y-2">
        <h1 className="text-2xl font-semibold tracking-tight">Handover code</h1>
        <p className="text-sm text-muted-foreground">
          For security, the handover code is shown only once and can&rsquo;t be retrieved. You can
          see its status here, or regenerate a new code to share.
        </p>
      </header>

      <div className="space-y-3 rounded-md border p-4 text-sm" data-testid="code-status">
        {used ? (
          <p role="status">
            The incoming doctor has already used this code and joined the handover. It can no longer
            be regenerated.
          </p>
        ) : expired ? (
          <p role="status">
            This code has <span className="font-medium text-foreground">expired</span>. Regenerate a
            new one to continue the handover.
          </p>
        ) : (
          <p role="status">
            This code is active. It expires in{" "}
            <span className="font-medium tabular-nums text-foreground">
              {formatRemaining(remainingMs)}
            </span>
            .
          </p>
        )}

        {locked && !used ? (
          <p className="text-muted-foreground">
            Code entry is temporarily locked after too many incorrect attempts. Regenerating clears
            the lock.
          </p>
        ) : !used && !expired && status.attemptsRemaining < 5 ? (
          <p className="text-muted-foreground">
            {status.attemptsRemaining}{" "}
            {status.attemptsRemaining === 1 ? "attempt remains" : "attempts remain"} before the code
            locks.
          </p>
        ) : null}
      </div>

      {error ? (
        <Alert variant="destructive">
          <AlertTitle>Couldn&rsquo;t regenerate the code</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      ) : null}

      {canRegenerate ? (
        <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
          <Button
            variant="outline"
            onClick={() => {
              setConfirmOpen(true);
            }}
            disabled={isRegenerating}
          >
            <RefreshCw className="mr-2 h-4 w-4" aria-hidden="true" />
            Regenerate code
          </Button>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Regenerate the handover code?</DialogTitle>
              <DialogDescription>
                This immediately invalidates the current code. Anyone you&rsquo;ve already shared it
                with will need the new code to join.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <DialogClose asChild>
                <Button variant="outline" disabled={isRegenerating}>
                  Cancel
                </Button>
              </DialogClose>
              <Button
                onClick={() => {
                  void handleRegenerate();
                }}
                disabled={isRegenerating}
              >
                {isRegenerating ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />
                ) : null}
                Regenerate code
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      ) : null}
    </div>
  );
}
